import axiosInstance from './axiosInstance';

/**
 * Business Onboarding API Service
 * Uses the app's main axios instance with built-in auth handling
 */

const businessOnboardingApi = {
  /**
   * Create a new business
   */
  createBusiness: async (data) => {
    const response = await axiosInstance.post('/businesses', data);
    return response.data;
  },

  /**
   * Get all businesses for a user
   */
  getUserBusinesses: async (userId) => {
    const response = await axiosInstance.get(`/users/${userId}/businesses`);
    return response.data;
  },

  /**
   * Get business details
   */
  getBusiness: async (businessId) => {
    const response = await axiosInstance.get(`/businesses/${businessId}`);
    return response.data;
  },

  /**
   * Update business information
   */
  updateBusiness: async (businessId, data) => {
    const response = await axiosInstance.patch(`/businesses/${businessId}`, data);
    return response.data;
  },

  /**
   * Complete onboarding (no document uploads; Stripe Connect is required only when enabling payments)
   */
  completeOnboarding: async (businessId) => {
    const response = await axiosInstance.post(`/businesses/${businessId}/complete`);
    return response.data;
  },

  /**
   * Get onboarding progress
   */
  getOnboardingProgress: async (businessId) => {
    const response = await axiosInstance.get(
      `/businesses/${businessId}/onboarding-progress`
    );
    return response.data;
  },

  /**
   * Upsert business location (step 2)
   */
  upsertLocation: async (businessId, data) => {
    const response = await axiosInstance.patch(`/businesses/${businessId}/location`, data);
    return response.data;
  },

  /**
   * Start €10 location-letter checkout
   */
  createLocationLetterCheckout: async (businessId) => {
    const response = await axiosInstance.post(`/businesses/${businessId}/location-letter/checkout`);
    return response.data;
  },

  /**
   * Confirm paid checkout and send location verification letter
   */
  confirmLocationLetterPayment: async (businessId, sessionId) => {
    const response = await axiosInstance.post(`/businesses/${businessId}/location-letter/confirm-payment`, { sessionId });
    return response.data;
  },

  /**
   * Get location-letter verification status
   */
  getLocationLetterStatus: async (businessId) => {
    const response = await axiosInstance.get(`/businesses/${businessId}/location-letter/status`);
    return response.data;
  },

  /**
   * Verify location using 6-character code from letter
   */
  verifyLocationLetterCode: async (businessId, code, gps = null) => {
    const payload = { code };
    if (gps && Number.isFinite(Number(gps.latitude)) && Number.isFinite(Number(gps.longitude))) {
      payload.gpsLatitude = Number(gps.latitude);
      payload.gpsLongitude = Number(gps.longitude);
    }
    const response = await axiosInstance.post(`/businesses/${businessId}/location-letter/verify-code`, payload);
    return response.data;
  },

  /**
   * Resend location verification letter
   */
  resendLocationLetter: async (businessId) => {
    const response = await axiosInstance.post(`/businesses/${businessId}/location-letter/resend`);
    return response.data;
  },

  /**
   * Get Stripe onboarding link
   */
  getStripeOnboardingLink: async (businessId) => {
    const response = await axiosInstance.get(
      `/businesses/${businessId}/stripe/onboarding-link`
    );
    return response.data;
  },

  /**
   * Check business capabilities
   */
  checkCapabilities: async (businessId) => {
    const response = await axiosInstance.get(`/businesses/${businessId}/capabilities`);
    return response.data;
  },

  /**
   * Upload file to cloud storage
   */
  uploadFile: async (file, folder = 'business-documents') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const response = await axiosInstance.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export default businessOnboardingApi;
